import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { CATEGORIES } from "../../lib/i18n";
import { useStore } from "../../context/StoreContext";
import { Avatar } from "../community/Bits";
import { PokemonMenu, MobileLangLinks } from "./PokemonMenu";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-white/15 p-0.5 text-xs font-semibold" data-testid="language-toggle">
      {["de", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}-btn`} onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full uppercase transition-colors ${lang === l ? "bg-[#F2C230] text-[#120E1F]" : "text-white/70 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { t } = useLang();
  const { prelaunch, free_shipping_de } = useStore();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobile, setMobile] = useState(false);

  const submit = (e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); setMobile(false); };
  const accountTo = user ? (user.role === "admin" ? "/admin" : "/member") : "/login";

  return (
    <header className="sticky top-0 z-50 bg-[#120E1F]/95 backdrop-blur-xl border-b border-[#8A5CD6]/30" data-testid="site-header">
      <div className="bg-gradient-to-r from-[#6E3FB8] via-[#8A5CD6] to-[#6E3FB8] text-white text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.2em] uppercase text-center py-1.5 truncate px-2 font-semibold">
        {prelaunch ? <a href="/#launch" data-testid="launch-bar-link">{t("launch_bar")}</a> : <>{t("free_ship_ok").replace("!", "")} · ≥ {free_shipping_de} €</>}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
        <Logo />
        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
            className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#F2C230]/60 transition-colors" />
        </form>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangToggle /></div>
          <Link to={accountTo} data-testid="header-account-link" className="p-2 text-white/80 hover:text-[#C9B6F2] transition-colors">{user && user.avatar ? <Avatar id={user.avatar} size="h-7 w-7" /> : <User className="h-5 w-5" />}</Link>
          <button data-testid="cart-open-btn" onClick={() => setOpen(true)} className="relative p-2 text-white/80 hover:text-[#C9B6F2] transition-colors">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-[#F2C230] text-[#120E1F] text-[10px] font-bold grid place-items-center">{count}</span>}
          </button>
          <button className="md:hidden p-2 text-white" data-testid="mobile-menu-btn" onClick={() => setMobile(!mobile)}>{mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>
      <nav className="hidden md:block border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-6 lg:gap-8 h-11 text-[13px] tracking-wide">
          <PokemonMenu />
          <NavLink to="/shop" end data-testid="nav-shop-link" className="text-white/70 hover:text-[#C9B6F2] transition-colors">{t("all")}</NavLink>
          {CATEGORIES.filter((c) => c !== "booster-bundles").map((c) => (
            <Link key={c} to={`/shop?category=${c}`} data-testid={`nav-cat-${c}`} className="text-white/70 hover:text-[#C9B6F2] transition-colors">{t("cat")[c]}</Link>
          ))}
          <Link to="/community" data-testid="nav-community-link" className="text-[#C9B6F2] hover:text-[#F2C230] transition-colors">Community</Link>
          <Link to="/about" data-testid="nav-about-link" className=" text-[#F2C230] hover:text-[#C9B6F2] transition-colors">{t("nav_about")}</Link>
        </div>
      </nav>
      {mobile && (
        <div className="md:hidden border-t border-white/10 px-4 py-4 space-y-3 bg-[#120E1F]" data-testid="mobile-menu">
          <form onSubmit={submit}><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="mobile-search-input"
            className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white" /></form>
          <MobileLangLinks onClick={() => setMobile(false)} />
          {CATEGORIES.map((c) => <Link key={c} onClick={() => setMobile(false)} to={`/shop?category=${c}`} className="block text-white/80 py-1">{t("cat")[c]}</Link>)}
          <Link onClick={() => setMobile(false)} to="/community" className="block text-[#C9B6F2] py-1" data-testid="mobile-community-link">Community</Link>
          <Link onClick={() => setMobile(false)} to="/about" className="block text-[#F2C230] py-1">{t("nav_about")}</Link>
          <LangToggle />
        </div>
      )}
    </header>
  );
};
